import React, { useState, useEffect, useCallback } from "react";
import { Route, Routes, useNavigate, useLocation, useParams } from "react-router-dom";
import { CircularProgress } from "@ann/web-components";
import { RolesPageContainer, LoadingContainer } from "./Roles.styled";
import { Overview, Permissions, Users } from "./pages";
import { Header, Tabs } from "./components";
import { AssignRoleModal } from "../../components";
import { RoleDetailsTabsEnum } from "./enums/roleDetailsTabs";
import { getRoleList, getRoleByUuid } from "../../services";

const Roles = ({ breadcrumbHelpers }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleId } = useParams();
  const [selectedTab, setSelectedTab] = useState(RoleDetailsTabsEnum.OVERVIEW);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  
  // Role data state - initialized from location.state if available
  const [roleData, setRoleData] = useState(location.state?.roleData || null);
  const [roleName, setRoleName] = useState(location.state?.roleName || "Role");
  const [roleDescription, setRoleDescription] = useState(location.state?.roleDescription || "");

  // Fetch role data if not available from navigation state
  const fetchRoleData = useCallback(async () => {
    if (!roleId) return;
    
    setIsLoading(true);
    try {
      // First, try to fetch the specific role by UUID
      try {
        const response = await getRoleByUuid(roleId);
        const statusCode = response.status_code || response.statusCode || 200;
        const role = response.body;
        
        if (statusCode >= 200 && statusCode < 300 && role) {
          setRoleData(role);
          setRoleName(role.name || "Role");
          setRoleDescription(role.description || "");
          return; // Success, exit early
        }
      } catch (getRoleByUuidError) {
        // If getRoleByUuid fails, fallback to getRoleList method
        console.warn("getRoleByUuid failed, falling back to getRoleList:", getRoleByUuidError);
        
        // Fetch all roles without search filter (fallback method)
        const response = await getRoleList({
          page: 1,
          page_size: 100, // Fetch enough to find the role
        });
        
        const statusCode = response.status_code || response.statusCode || 200;
        const roles = response.body || [];
        
        if (statusCode >= 200 && statusCode < 300 && roles.length > 0) {
          // Find the role by UUID in the results
          const foundRole = roles.find(
            (role) => role.roleUuid === roleId || role.id === roleId
          );
          
          if (foundRole) {
            setRoleData(foundRole);
            setRoleName(foundRole.name || "Role");
            setRoleDescription(foundRole.description || "");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching role data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [roleId]);

  // Fetch role data on mount if not available from state
  useEffect(() => {
    if (!location.state?.roleData && roleId) {
      fetchRoleData();
    }
  }, [location.state?.roleData, roleId, fetchRoleData]);

  // Mapeo de tabs a rutas URL
  const getTabRoute = (tabIndex) => {
    switch (tabIndex) {
      case RoleDetailsTabsEnum.OVERVIEW:
        return "";
      case RoleDetailsTabsEnum.PERMISSIONS:
        return "permissions";
      case RoleDetailsTabsEnum.USERS:
        return "users";
      default:
        return "";
    }
  };

  const getTabFromRoute = (pathname) => {
    if (pathname.includes("/permissions")) {
      return RoleDetailsTabsEnum.PERMISSIONS;
    } else if (pathname.endsWith("/users") || pathname.includes("/users/")) {
      return RoleDetailsTabsEnum.USERS;
    } else {
      return RoleDetailsTabsEnum.OVERVIEW;
    }
  };

  const getTabLabel = (tabIndex) => {
    switch (tabIndex) {
      case RoleDetailsTabsEnum.OVERVIEW:
        return "Overview";
      case RoleDetailsTabsEnum.PERMISSIONS:
        return "Permissions";
      case RoleDetailsTabsEnum.USERS:
        return "Users";
      default:
        return "Overview";
    }
  };

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    setSelectedTab(tabFromUrl);
  }, [location.pathname]);

  useEffect(() => {
    const currentTab = getTabFromRoute(location.pathname);
    const tabLabel = getTabLabel(currentTab);
    
    breadcrumbHelpers?.setPageBreadcrumb?.("Roles", roleName, {
      pageHref: "/v2/user-management/roles",
      subHref: `/v2/user-management/roles/${roleId}`,
      tabLabel: tabLabel,
      tabHref: location.pathname,
    });
  }, [breadcrumbHelpers, location.pathname, roleName, roleId]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    const newRoute = getTabRoute(index);
    
    // Build base path with roleId
    const basePath = `/v2/user-management/roles/${roleId}`;
    
    if (newRoute) {
      navigate(`${basePath}/${newRoute}`, { 
        replace: true,
        state: { roleName, roleDescription, roleData }
      });
    } else {
      navigate(basePath, { 
        replace: true,
        state: { roleName, roleDescription, roleData }
      });
    }
  };

  // Callback to update role data after successful edit
  const handleRoleUpdate = useCallback((updatedData) => {
    if (updatedData.name) {
      setRoleName(updatedData.name);
    }
    if (updatedData.description !== undefined) {
      setRoleDescription(updatedData.description);
    }
    // Also update roleData if it exists
    if (roleData) {
      setRoleData(prev => ({
        ...prev,
        name: updatedData.name || prev?.name,
        description: updatedData.description ?? prev?.description,
      }));
    }
  }, [roleData]);

  // Common props for all tab components
  const roleProps = { 
    roleId, 
    roleName, 
    roleDescription, 
    roleData,
    onRoleUpdate: handleRoleUpdate
  };

  // Show loading while fetching role data
  if (isLoading) {
    return (
      <RolesPageContainer>
        <LoadingContainer>
          <CircularProgress size="xl" />
        </LoadingContainer>
      </RolesPageContainer>
    );
  }

  return (
    <RolesPageContainer>
      <Header 
        title={roleName} 
        subtitle={roleDescription} 
        onAssignClick={() => setIsAssignModalOpen(true)}
      />
      <Tabs key={selectedTab} onTabChange={handleTabChange} defaultIndex={selectedTab} />
      {selectedTab === RoleDetailsTabsEnum.OVERVIEW ? (
        <Overview {...roleProps} />
      ) : (
        <Routes>
          <Route path="permissions" element={<Permissions {...roleProps} />} />
          <Route path="users" element={<Users {...roleProps} />} />
        </Routes>
      )}

      <AssignRoleModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        roleUuid={roleId}
        roleName={roleName}
        onAssign={(users) => {
          console.log("Assigning users to role:", users);
          // TODO: Implement API call to assign users to role
        }}
      />
    </RolesPageContainer>
  );
};

export default Roles;
